/**
 * useKanbanData — Hook para datos Kanban (0258)
 * Fetch: tags/kanban, tickets. Incluye agendaActive y profileName del response.
 */
import { useState, useEffect, useCallback } from "react";
import api from "../../../services/api";
import logger from "../../../utils/logger";

export default function useKanbanData() {
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [agendaActive, setAgendaActive] = useState(false);
  const [profileName, setProfileName] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get("/tags/kanban");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      setAgendaActive(Boolean(response.data.agendaActive));
      setProfileName(response.data.profileName ?? null);
      return fetchedTags;
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        return [];
      }
      logger.dashboard.error("Error cargando tags Kanban:", error);
      return [];
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      let pageNumber = 1;
      let hasMore = true;
      const allTickets = [];

      while (hasMore && pageNumber <= 20) {
        const { data } = await api.get("/ticket/kanban", {
          params: { pageNumber },
        });
        allTickets.push(...(data.tickets || []));
        hasMore = Boolean(data.hasMore);
        pageNumber += 1;
      }

      setTickets(allTickets);
      return allTickets;
    } catch (err) {
      logger.dashboard.error("Error cargando tickets Kanban:", err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) return;
      setTickets([]);
    }
  }, []);

  const initializeData = useCallback(async () => {
    if (isInitialized) return;
    try {
      await fetchTags();
      await fetchTickets();
      setIsInitialized(true);
    } catch (error) {
      logger.dashboard.error("Error inicializando Kanban:", error);
    }
  }, [fetchTags, fetchTickets, isInitialized]);

  useEffect(() => {
    let mounted = true;
    const timer = setTimeout(() => {
      if (mounted && !isInitialized) initializeData();
    }, 500);
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [initializeData, isInitialized]);

  const refetch = useCallback(async () => {
    await fetchTags();
    await fetchTickets();
  }, [fetchTags, fetchTickets]);

  return {
    tags,
    setTags,
    tickets,
    setTickets,
    agendaActive,
    profileName,
    isInitialized,
    refetch,
  };
}
